import { useTranslation } from "react-i18next";
import type { SectorWords } from "@/hooks/use-sector-words";

interface EmailPreviewProps {
  words: SectorWords;
  toName: string;
  toEmail: string;
  fromName: string;
  unitName: string;
  greeting: string;
}

/** The invitation email as the first recipient will see it; only the greeting is editable (elsewhere). */
export function EmailPreview({ words, toName, toEmail, fromName, unitName, greeting }: EmailPreviewProps) {
  const { t } = useTranslation("onboarding");
  const first = toName.trim().split(/\s+/)[0] || toName;
  return (
    <div className="rounded-g-lg border border-g-line bg-g-bg-input" data-testid="onboarding-email-preview">
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 border-b border-g-line px-4 py-3 text-[12.5px] text-g-ink-600">
        <dt className="font-semibold text-g-ink-500">{t("invite.email.fromLabel")}</dt>
        <dd className="truncate">{t("invite.email.from", { name: fromName })}</dd>
        <dt className="font-semibold text-g-ink-500">{t("invite.email.toLabel")}</dt>
        <dd className="truncate">{toEmail}</dd>
        <dt className="font-semibold text-g-ink-500">{t("invite.email.subjectLabel")}</dt>
        <dd className="truncate font-semibold text-g-ink-900">{t("invite.email.subject", { first })}</dd>
      </dl>
      <div className="flex flex-col gap-3 px-4 py-4 text-[14px] leading-relaxed text-g-ink-700">
        <p className="font-semibold text-g-ink-900">{t("invite.email.hello", { first })}</p>
        {greeting.trim() ? (
          <p className="whitespace-pre-line rounded-g-sm border-l-2 border-g-primary bg-g-bg-card px-3 py-2 italic text-g-ink-700">
            {greeting.trim()}
          </p>
        ) : null}
        <p>{t("invite.email.p45")}</p>
        <p>{t("invite.email.noRightWrong")}</p>
        <p>{t("invite.email.yours")}</p>
        <p className="rounded-g-sm bg-g-primary-wash px-3 py-2 text-[13px] text-g-ink-600">
          {t("invite.email.ai", { chefDef: words.chefDef })}
        </p>
        <span className="inline-flex w-fit rounded-g-md bg-g-primary px-4 py-2 text-[14px] font-semibold text-g-on-primary">
          {t("invite.email.cta")}
        </span>
        <p className="text-[12.5px] text-g-ink-500">{t("invite.email.link14", { email: toEmail })}</p>
        <p>
          {t("invite.email.regards")}
          <br />
          {t("invite.email.signature", { name: fromName, unit: unitName })}
        </p>
      </div>
    </div>
  );
}
